import React, { Suspense, useMemo } from "react"
import moment from "moment"
import { Box, Collapse } from "@material-ui/core"
import { makeStyles } from "@material-ui/styles"
import { ModuleHeading, LinearProgress, Shine } from ".."
import TimeAgo from "../TimeAgo"
import { useJobUtils } from "../../utils"
import { JobScheduledMessage } from "./JobScheduledMessage"
import { ProcessSteps } from "./ProcessSteps"
import { JobMoreInformation } from "./JobMoreInformation"
import { useMutationUpdateJob } from "../../data/jobs/useMutationUpdateJob"
import PageLoadingSpinner from "../LoadingSpinner/PageLoadingSpinner"

const VirtualProcessSteps = React.lazy(() => import("./VirtualProcessSteps"))

const virtualStepMinimum = 20

const useStyles = makeStyles((theme) => ({
  detail: {
    backgroundColor: theme.palette.incomplete.background,
    border: `solid 2px ${theme.palette.incomplete.background}`,
    borderRadius: "8px",
  },
  detailOverdue: {
    backgroundColor: theme.palette.warning.background,
    border: `solid 1px ${theme.palette.warning.main}`,
  },
  detailCompleted: {
    backgroundColor: "#f5fffc",
    border: "solid 2px #18ce99",
  },
  detailInner: {
    padding: "20px 24px",
  },
  detailName: {
    fontSize: 26,
    lineHeight: "36px",
    marginBottom: theme.spacing(2),
  },
  detailSteps: {
    display: "flex",
    flexDirection: "row",
    fontSize: 16,
  },
  incomplete: {
    color: ({ overdue }) => theme.palette[overdue ? "warning" : "primary"].main,
    fontWeight: "600",
  },
  success: {
    color: theme.palette.success.main,
    fontWeight: "600",
  },
}))

const ProcessViewer = ({ job, isInline, onDelete, scrollableTarget }) => {
  const { isJobAfterToday, isJobOverdue } = useJobUtils()
  const [updateJob] = useMutationUpdateJob(job)
  const {
    title,
    percentComplete,
    status: { completed, steps },
    availableFrom,
  } = job
  const scheduled = isJobAfterToday(availableFrom)
  const overdue = isJobOverdue(job)
  const classes = useStyles({ overdue })
  const expansionClasses = {
    root: classes.expansionRoot,
    icon: classes.expansionIcon,
    summary: classes.expansionSummary,
    detail: classes.expansionDetail,
  }

  const [completedCount, lastStepCompletedAt] = useMemo(() => {
    let at
    const count = steps.filter((step) => {
      if (step.completedAt && (!at || moment(step.completedAt).isAfter(at))) at = step.completedAt
      return step.completedAt
    }).length

    return [count, at]
  }, [steps])

  const handleUpdateTitle = async (newTitle) => {
    if (newTitle === title) {
      return
    }
    await updateJob({ variables: { id: job.id, title: newTitle } })
  }

  const handleDelete = () => {
    onDelete && onDelete()
  }

  const processHeader = (
    <>
      <Box
        className={`${classes.detail} ${completed ? classes.detailCompleted : ""} ${
          overdue ? classes.detailOverdue : ""
        }`}
      >
        <Shine go={completed}>
          <Box className={classes.detailInner}>
            <ModuleHeading
              id="process-title"
              className={classes.detailName}
              value={title}
              editable={!scheduled && "job_update_all"}
              onEdited={handleUpdateTitle}
              noMargin
            />
            {!scheduled && (
              <>
                <Box mb={1.5}>
                  <LinearProgress value={percentComplete} showValue overdue={overdue} />
                </Box>
                <Box className={classes.detailSteps} mb={1}>
                  <Box className={completed ? classes.success : classes.incomplete} mr={1}>
                    {completedCount}/{steps.length}
                  </Box>
                  <Box>steps have been completed.</Box>
                </Box>
                <Collapse in={!!lastStepCompletedAt}>
                  <Box>
                    <strong>Most recent completion:</strong> <TimeAgo date={lastStepCompletedAt} />
                  </Box>
                </Collapse>
              </>
            )}
            {scheduled && <JobScheduledMessage type="Process" availableFrom={availableFrom} />}
          </Box>
        </Shine>
      </Box>

      <JobMoreInformation
        job={job}
        isInline={isInline}
        classes={classes}
        expansionClasses={expansionClasses}
        onDelete={handleDelete}
      />
    </>
  )

  if (steps.length < virtualStepMinimum) {
    return (
      <>
        {processHeader}
        <ProcessSteps
          job={job}
          steps={job.status.steps}
          classes={classes}
          isScheduled={scheduled}
          scrollableTarget={scrollableTarget}
        />
      </>
    )
  }

  return (
    <Suspense fallback={<PageLoadingSpinner />}>
      <VirtualProcessSteps
        header={processHeader}
        job={job}
        steps={job.status.steps}
        classes={classes}
        isScheduled={scheduled}
        scrollableTarget={scrollableTarget}
      />
    </Suspense>
  )
}

export { ProcessViewer, virtualStepMinimum as processVirtualStepMinimum }
